// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@eisinc/App/Components/ProductGridCard/ProductGridContainer";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CarouselCardType } from "@eisinc/XDL/types";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

export interface ProductCarouselProductSkeletonStyles {
    productGridContainerStyles?: ProductGridContainerStyle;
    wrapper?: InjectableCss;
    partNumbersAndBrandWrapper?: InjectableCss;
    imageBadgesWrapper?: InjectableCss;
}

const productCarouselProductSkeletonStyles: ProductCarouselProductSkeletonStyles = {
    wrapper: {
        css: css`
            width: ${({ view }: any) => (view === "grid" ? 317 : 328)}px;
        `,
    },
    partNumbersAndBrandWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 6px;
            margin-bottom: 6px;
        `,
    },
    imageBadgesWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
};

export interface ProductCarouselProductSkeletonProps {
    showImage?: boolean;
    showBrand?: boolean;
    showTitle?: boolean;
    showPartNumbers?: boolean;
    showPrice?: boolean;
    showAddToCart?: boolean;
    showAddToList?: boolean;
    showImageBadges?: boolean;
    showAvailability?: boolean;
    view?: CarouselCardType;
    extendedStyles?: ProductCarouselProductSkeletonStyles;
}

const ProductCarouselProductSkeleton = ({
    showImage = true,
    showBrand = true,
    showTitle = true,
    showPartNumbers = true,
    showPrice = true,
    showAddToCart = true,
    showAddToList = true,
    showImageBadges = false,
    showAvailability = false,
    view,
    extendedStyles,
}: ProductCarouselProductSkeletonProps) => {
    const styles = mergeToNew(productCarouselProductSkeletonStyles, extendedStyles);
    const imageSize = view === "condensed" ? 204 : view === "miniCard" ? 116 : 317;

    return (
        <StyledWrapper {...styles.wrapper} {...{ view }}>
            <ProductGridContainer extendedStyles={styles.productGridContainerStyles}>
                <ProductGridContainer.Image>
                    {showImage && <Skeleton height={`${imageSize}px`} width={`${imageSize}px`} />}
                </ProductGridContainer.Image>
                <ProductGridContainer.Info>
                    {showTitle && <Skeleton height="36px" width="90%" />}
                    <StyledWrapper {...styles.partNumbersAndBrandWrapper}>
                        {showPartNumbers && (
                            <>
                                <Skeleton height="14px" width="60%" />
                                <Skeleton height="14px" width="45%" />
                            </>
                        )}
                        {showBrand && <Skeleton height="14px" width="50%" />}
                    </StyledWrapper>
                </ProductGridContainer.Info>
                <ProductGridContainer.Price>
                    {showPrice && <Skeleton height="24px" width="130px" />}
                </ProductGridContainer.Price>
                <ProductGridContainer.AddToCartActions>
                    {showAddToCart && <Skeleton height="26px" />}
                </ProductGridContainer.AddToCartActions>
                <ProductGridContainer.Availability>
                    {showAvailability && <Skeleton height="14px" width="150px" />}
                    {showImageBadges && (
                        <StyledWrapper {...styles.imageBadgesWrapper}>
                            <Skeleton height="24px" width="60px" />
                        </StyledWrapper>
                    )}
                </ProductGridContainer.Availability>
                <ProductGridContainer.AddToList>
                    {showAddToList && <Skeleton height="14px" />}
                </ProductGridContainer.AddToList>
            </ProductGridContainer>
        </StyledWrapper>
    );
};

export default ProductCarouselProductSkeleton;
